import React, { forwardRef, useState, useEffect } from 'react'
import { TouchableOpacity } from 'react-native'
import { useBaseInputStyle } from '@linqpal/common-frontend/src/ions/style'
import {
  InputText as AtomicInputText,
  CloseCircle,
} from '@linqpal/common-frontend/src/ui'
import {
  useFocusedErrorProps,
  useInputValidateInput,
  useInputValidateInputOnBlur,
  withEditableConverter,
} from '@linqpal/common-frontend/src/hooks'
import {
  InputFieldContainer,
  InputFieldOuterContainer,
} from './InputFieldContainer'
import { composeStyle } from '@linqpal/common-frontend/src/helpers'
import { IconDropDown } from '../../../assets/icons'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { TextInputMaskTypeProp } from 'react-native-masked-text'
import { CurrencyInputProps } from 'react-currency-input-field'

/**
 *
 * @param props
 * @param props.style
 * @param props.inputMask
 * @param props.maskType
 * @param props.maskOptions
 * @param props.nativeProps
 * @param props.error
 * @param { boolean } props.error - This will be the initial Error value overridden by validateInput function.
 * @returns {JSX.Element}
 * @constructor
 */

type PropTypes = {
  style?: any
  label: string
  model: any
  name: string
  inputLength?: number
  disabled?: boolean
  mandatory?: boolean
  secureTextEntry?: boolean
  sibling?: React.ReactNode
  editable?: boolean
  showClearIcon?: boolean
  maskOptions?: any
  nativeProps?: any
  onChangeText?: (a) => void
  setInvalid?: (a) => void
  validateInput?: (a) => boolean | string
  onFocus?: () => void
  onBlur?: () => void
  inputMask?: string | boolean
  toRemoveRegex?: RegExp | string
  error?: string
  value?: string
  siblingProps?: any
  textStyle?: any
  highlight?: boolean
  showDropdown?: boolean
  onPress?: () => void
  validateInputOnBlur?: boolean
  inputOuterStyle?: any
  testID?: string
  maskType?: TextInputMaskTypeProp
  intlConfig?: CurrencyInputProps['intlConfig']
}

export const AppInputBase = forwardRef(
  (
    {
      style,
      error: initialError,
      onFocus: onInputFocus,
      onBlur: onInputBlur,
      siblingProps,
      onChangeText,
      toRemoveRegex,
      highlight,
      mandatory,
      inputOuterStyle,
      ...props
    }: PropTypes,
    ref,
  ) => {
    const { inputBackgroundColor } = useBaseInputStyle()
    const [error, setError] = React.useState(initialError)
    const {
      onBlur,
      onFocus,
      focused,
      error: focusedError,
    } = useFocusedErrorProps(error)

    const {
      value,
      label,
      validateInput,
      validateInputOnBlur,
      editable = true,
    } = props

    const [blur, setBlur] = useState(false)

    const finalOnFocus = () => {
      onInputFocus && onInputFocus()
      onFocus()
    }

    const newOnBlur = useInputValidateInputOnBlur({
      validateInput,
      validateInputOnBlur,
      onInputBlur,
      setError,
      toRemoveRegex,
    })
    const finalOnBlur = (e) => {
      newOnBlur(e.nativeEvent.text)
      onBlur()
      setBlur(true)
    }

    const newOnChangeText = useInputValidateInput({
      validateInput,
      validateInputOnBlur,
      onChangeText,
      setError,
      toRemoveRegex,
    })

    const valid = !!value && (!validateInput || !validateInput(value))
    useEffect(() => {
      blur && newOnChangeText && newOnChangeText(value || '')
    }, [blur, valid, newOnChangeText, value])

    useEffect(() => {
      error && setBlur(true)
    }, [error])

    return (
      <InputFieldOuterContainer
        focused={focused}
        style={[
          style,
          !editable && { borderColor: '#F5F7F8' },
          highlight ? { borderColor: '#DD4A2C' } : {},
        ]}
        error={validateInputOnBlur ? focusedError : error}
      >
        <AtomicInputText
          // eslint-disable-next-line i18next/no-literal-string
          underlineColor="transparent"
          labelActiveColor={commonColors.accentText}
          onFocus={finalOnFocus}
          onBlur={finalOnBlur}
          disabledStyle={{ color: '#335C75' }}
          inputOuterStyle={composeStyle(
            inputBackgroundColor,
            { flex: 1, borderRadius: 8 },
            inputOuterStyle,
            !editable && { backgroundColor: '#F5F7F8' },
          )}
          theme={{ roundness: 8 }}
          dense
          sibling={
            props.showDropdown ? (
              <TouchableOpacity onPress={props.onPress} disabled={!editable}>
                <IconDropDown
                  width={15}
                  style={{ marginRight: 15, marginLeft: 15, marginTop: 2 }}
                />
              </TouchableOpacity>
            ) : editable === undefined || editable || props.showClearIcon ? (
              <CloseCircle
                value={value}
                disabled={props.disabled}
                onChangeText={onChangeText}
                testID={`${props?.testID}_clear_btn`}
                {...1}
              />
            ) : null
          }
          InputContainer={InputFieldContainer}
          error={validateInputOnBlur ? focusedError : error}
          {...props}
          label={`${label} ${(mandatory && ' *') || ''}`}
          ref={ref}
          onChangeText={newOnChangeText}
        />
      </InputFieldOuterContainer>
    )
  },
)

export const AppInputField = withEditableConverter(AppInputBase)
